import { useMemo } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";

import { token } from "@atlaskit/tokens";

import { partnerTemplateCardRelayFragment$key } from "@/__generated__/partnerTemplateCardRelayFragment.graphql";
import { ConfluenceTemplateAnalytics } from "@/components/confluence/templates/util/confluence-templates-analytics";
import { Link } from "@/components/wpl/link/src";
import type { Template } from "@/content/templates/template.types";
import { useLocalizedHref } from "@/libraries/web-framework";
import { DataEventProps } from "@/libraries/wpl/foundations/src";
import { testPartnerTemplatesData } from "@/templates/software/confluence/templates/confluence-templates-page/src/test-data/test-data";

export type PartnerTemplate = Template & {
    partnerDescription: string;
    partnerImage: {
        url: string;
    };
};

export interface PartnerTemplateCardProps {
    template: partnerTemplateCardRelayFragment$key;
}

export const fontCharlieText =
    '"Charlie Text", -apple-system, BlinkMacSystemFont, "Segoe UI",' +
    'Roboto, "Noto Sans", Ubuntu, "Droid Sans", "Helvetica Neue", sans-serif !important';

export const fontCharlieDisplay =
    '"Charlie Display", "Charlie Text", -apple-system, BlinkMacSystemFont, "Segoe UI",' +
    'Roboto, "Noto Sans", Ubuntu, "Droid Sans", "Helvetica Neue", sans-serif !important';

const partnerTemplateCardWrapperStyles = css({
    "display": "flex",
    "margin": "0 20px",
    "boxShadow": "1px 1px 6px rgba(0,0,0,0.1)",
    "@media (max-width: 39.99rem)": {
        flexDirection: "column",
        alignItems: "center"
    }
});

const partnerTemplateCardContentStyles = css({
    "display": "flex",
    "width": "480px",
    "flexDirection": "column",
    "justifyContent": "space-between",
    "alignItems": "flex-start",
    "padding": "40px",
    "backgroundColor": "#fff",
    "@media (max-width: 39.99rem)": {
        width: "100%",
        height: "288px",
        padding: "40px 20px 20px"
    },
    "@media (min-width: 40rem) and (max-width: 64rem)": {
        width: "50%"
    }
});

const partnerTemplateCardLogoStyles = css({
    "maxWidth": "200px",
    "maxHeight": "40px",
    "width": "auto",
    "height": "auto",
    "verticalAlign": "bottom",
    "@media (max-width: 39.99rem)": {
        height: "32px"
    }
});

const partnerTemplateCardInfoStyles = css({
    "display": "flex",
    "flexDirection": "column",
    "justifyContent": "space-between",
    "alignItems": "flex-start",
    "height": "190px",
    "@media (max-width: 39.99rem)": {
        height: "130px"
    }
});

const partnerTemplateCardTitleStyles = css({
    "fontSize": "24px",
    "fontWeight": "500",
    "letterSpacing": ".36px",
    "color": token("color.text.brand", "#172b4d"),
    "fontFamily": fontCharlieDisplay,
    "lineHeight": "1.2",
    "margin": 0,
    "@media (max-width: 39.99rem)": {
        fontSize: "20px",
        color: token("color.text.brand", "#091e42"),
        lineHeight: "24px"
    }
});

const partnerTemplateCardDescriptionStyles = css({
    "fontSize": "20px",
    "lineHeight": "24px",
    "color": "#172b4d",
    "margin": 0,
    "fontFamily": fontCharlieText,
    "@media (max-width: 39.99rem)": {
        fontSize: "16px",
        lineHeight: "16px",
        color: token("color.text.brand", "#091e42")
    },
    "@media (min-width: 40rem) and (max-width: 63.99rem)": {
        display: "-webkit-box",
        WebkitBoxOrient: "vertical",
        WebkitLineClamp: "5",
        overflow: "hidden",
        lineHeight: "20px"
    }
});

const cardLink = css({
    "display": "block",
    "color": "#0052cc",
    "fontWeight": 500,
    "zIndex": 1,
    "lineHeight": 1,
    "fontFamily": fontCharlieText,
    "@media (max-width: 39.99rem)": {
        fontSize: "12px"
    }
});

const PartnerTemplateCardFragment = graphql`
    fragment partnerTemplateCardRelayFragment on ContentPlatformTemplate {
        templateId
        urlSlug
        name
        oneLinerHeadline
    }
`;

export const PartnerTemplateCard = ({ template }: PartnerTemplateCardProps) => {
    const data = useFragment(PartnerTemplateCardFragment, template);

    // Note that some fields are still using the existing hardcoded partner template data,
    // as the images or icons are not rendering properly.
    const testData = useMemo(() => {
        const testData = testPartnerTemplatesData.find(
            (x) => x.urlSlug === data.urlSlug
        );
        // Use CPAPI data where possible, otherwise use hardcoded data
        return {
            templateId: data.templateId,
            name: data.name,
            urlSlug: data.urlSlug,
            partnerDescription: data.oneLinerHeadline,
            contributor: testData?.contributor,
            previewBlurb: data.oneLinerHeadline,
            templateIcon: testData?.templateIcon,
            partnerImage: testData?.partnerImage
        };
    }, [data]);

    const intl = useIntl();
    const localizedHref = useLocalizedHref(
        `/software/confluence/templates/${testData.urlSlug}`
    );

    const partnerTemplateImageStyles = css({
        "width": "480px",
        "height": "400px",
        "backgroundPosition": "center",
        "backgroundSize": "cover",
        "@media (max-width: 39.99rem)": {
            width: "100%",
            height: "192px"
        },
        "@media (min-width: 40rem) and (max-width: 63.99rem)": {
            width: "50%"
        },
        "backgroundImage": `url(${testData.partnerImage?.url})`
    });

    const viewTemplateText = intl.formatMessage({
        id: "confluence.templates.partner-template-card.view-link",
        defaultMessage: "View template"
    });

    return (
        <div css={partnerTemplateCardWrapperStyles}>
            <div css={partnerTemplateImageStyles} />
            <div css={partnerTemplateCardContentStyles}>
                {/* eslint-disable-next-line no-restricted-syntax */}
                <img
                    css={partnerTemplateCardLogoStyles}
                    src={testData.templateIcon?.url}
                    alt={`${testData.contributor?.name || "Partner"} logo`}
                />
                <div css={partnerTemplateCardInfoStyles}>
                    <h4 css={partnerTemplateCardTitleStyles}>
                        {testData.name}
                    </h4>
                    <p css={partnerTemplateCardDescriptionStyles}>
                        {testData.partnerDescription}
                    </p>
                    <div css={cardLink}>
                        <Link
                            text={viewTemplateText}
                            href={localizedHref}
                            dataEventProps={
                                {
                                    ...ConfluenceTemplateAnalytics.viewPartnerTemplate(
                                        testData.urlSlug
                                    ),
                                    "data-event": "clicked",
                                    "data-event-component": "link"
                                } as DataEventProps
                            }
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
